/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import { ICoreContext } from '@msdyn365-commerce/core';
import * as React from 'react';
import { debounce } from 'lodash';

import { IBusinessSignUpItem } from '@msdyn365-commerce-modules/business-account-management';
import { getEntityAsync } from '../../../actions/get-entity';
import { searchEntitiesByNameAsync } from '../../../actions/search-entities-by-name';
import { IEntity } from '../../../actions/entities/entity';
import { IBusinessSignUpResources } from '../definition-extensions/business-sign-up.ext.props.autogenerated';
import * as GTM from '../../../shared-utils/google-tag-manager/gtm';
import { IBusinessSignUpViewProps } from '../../../modules/dlvry-business-sign-up/dlvry-business-sign-up';

interface IBusinessSignUpFeatureConfig {
    enableDepartmentSearch?: boolean;
    enableAddressSearch?: boolean;
}

interface IBusinessSignUpItemWithResources extends IBusinessSignUpItem {
    context: ICoreContext;
    config: object;
    inputId?: string;
    resources?: IBusinessSignUpResources;
}

type DepartmentSearchStatus = 'idle' | 'searching' | 'match' | 'no-match';
type CompanyNameSearchStatus = 'idle' | 'searching' | 'matches' | 'no-match';

function setValueAndTriggerEvent(selector: string, value: string) {
    const element = document.querySelector<HTMLInputElement>(selector);

    if (!element) {
        return;
    }

    const nativeInputValueSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value')?.set;
    nativeInputValueSetter?.call(element, value);
    element.dispatchEvent(new CustomEvent('change', { detail: { preventAutoCompleteSearch: true }, bubbles: true }));
}

const BusinessSignUpItem: React.FC<IBusinessSignUpItemWithResources> = ({
    errorMessage,
    context,
    config,
    inputId,
    input,
    isAddressInput,
    label,
    resources,
    wrapper
}) => {
    const featureConfig = config as IBusinessSignUpFeatureConfig;
    const isDepartmentSearchEnabled = Boolean(featureConfig.enableDepartmentSearch);
    const isAddressSearchEnabled = featureConfig.enableAddressSearch === true;
    const [departmentSearchStatus, setDepartmentSearchStatus] = React.useState<DepartmentSearchStatus>('idle');
    const [matchedEntity, setMatchedEntity] = React.useState<IEntity | undefined>();
    const [companyNameSearchStatus, setCompanyNameSearchStatus] = React.useState<CompanyNameSearchStatus>('idle');
    const [nameMatches, setNameMatches] = React.useState<IEntity[]>([]);
    const [activeCompanyNameMatchIndex, setActiveCompanyNameMatchIndex] = React.useState(-1);
    const latestSearch = React.useRef(0);
    const latestCompanyNameSearch = React.useRef(0);
    const lastDepartmentSearchStatus = React.useRef<DepartmentSearchStatus>('idle');
    const lastCompanyNameSearchStatus = React.useRef<CompanyNameSearchStatus>('idle');

    const doDepartmentSearch = React.useCallback(
        debounce(async (organizationNumber: string, searchId: number) => {
            const orgNumber = organizationNumber.replace(/\D/g, '').slice(-9);

            if (orgNumber.length !== 9) {
                return;
            }

            try {
                const result = await getEntityAsync({ callerContext: context.actionContext }, orgNumber);
                if (latestSearch.current !== searchId) {
                    return;
                }

                if (result.Name) {
                    setMatchedEntity(result);
                    lastDepartmentSearchStatus.current = 'match';
                    setDepartmentSearchStatus('match');
                } else {
                    setMatchedEntity(undefined);
                    lastDepartmentSearchStatus.current = 'no-match';
                    setDepartmentSearchStatus('no-match');
                }
            } catch {
                if (latestSearch.current === searchId) {
                    setMatchedEntity(undefined);
                    lastDepartmentSearchStatus.current = 'no-match';
                    setDepartmentSearchStatus('no-match');
                }
            }
        }, 1000),
        [context.actionContext]
    );

    React.useEffect(() => () => doDepartmentSearch.cancel(), [doDepartmentSearch]);

    const doCompanyNameSearch = React.useCallback(
        debounce(async (companyName: string, searchId: number) => {
            if (companyName.trim().length < 2) {
                return;
            }

            try {
                const results = await searchEntitiesByNameAsync(
                    {
                        callerContext: context.actionContext,
                        queryResultSettings: {
                            Paging: { Top: 6 },
                            count: true
                        }
                    },
                    companyName.trim()
                );
                if (latestCompanyNameSearch.current !== searchId) {
                    return;
                }

                const matches = results.filter(result => result.Name);
                setNameMatches(matches);
                setActiveCompanyNameMatchIndex(-1);
                lastCompanyNameSearchStatus.current = matches.length ? 'matches' : 'no-match';
                setCompanyNameSearchStatus(lastCompanyNameSearchStatus.current);
            } catch {
                if (latestCompanyNameSearch.current === searchId) {
                    setNameMatches([]);
                    setActiveCompanyNameMatchIndex(-1);
                    lastCompanyNameSearchStatus.current = 'no-match';
                    setCompanyNameSearchStatus('no-match');
                }
            }
        }, 400),
        [context.actionContext]
    );

    React.useEffect(() => () => doCompanyNameSearch.cancel(), [doCompanyNameSearch]);

    const dismissDepartmentSearch = React.useCallback(() => {
        latestSearch.current += 1;
        doDepartmentSearch.cancel();
        setDepartmentSearchStatus('idle');
    }, [doDepartmentSearch]);

    const dismissCompanyNameSearch = React.useCallback(() => {
        latestCompanyNameSearch.current += 1;
        doCompanyNameSearch.cancel();
        setActiveCompanyNameMatchIndex(-1);
        setCompanyNameSearchStatus('idle');
    }, [doCompanyNameSearch]);

    React.useEffect(() => {
        if (inputId !== 'Department' || !isDepartmentSearchEnabled) {
            return;
        }

        const dismissWhenOutside = (event: Event) => {
            const departmentInput = document.getElementById('Department');
            const departmentItem = departmentInput?.closest('.department-search-open');
            const target = event.target as HTMLElement | null;

            if (event.type === 'focusin' && target === departmentInput && lastDepartmentSearchStatus.current !== 'idle') {
                setDepartmentSearchStatus(lastDepartmentSearchStatus.current);
                return;
            }

            if (departmentItem && target && !departmentItem.contains(target)) {
                dismissDepartmentSearch();
            }
        };

        document.addEventListener('mousedown', dismissWhenOutside);
        document.addEventListener('focusin', dismissWhenOutside);

        return () => {
            document.removeEventListener('mousedown', dismissWhenOutside);
            document.removeEventListener('focusin', dismissWhenOutside);
        };
    }, [dismissDepartmentSearch, inputId, isDepartmentSearchEnabled]);

    React.useEffect(() => {
        if (inputId !== 'CompanyName' || !isDepartmentSearchEnabled) {
            return;
        }

        const dismissWhenOutside = (event: Event) => {
            const companyNameInput = document.getElementById('CompanyName');
            const companyNameItem = companyNameInput?.closest('.company-name-search-open');
            const target = event.target as HTMLElement | null;

            if (event.type === 'focusin' && target === companyNameInput && lastCompanyNameSearchStatus.current !== 'idle') {
                setCompanyNameSearchStatus(lastCompanyNameSearchStatus.current);
                return;
            }

            if (companyNameItem && target && !companyNameItem.contains(target)) {
                dismissCompanyNameSearch();
            }
        };

        document.addEventListener('mousedown', dismissWhenOutside);
        document.addEventListener('focusin', dismissWhenOutside);

        return () => {
            document.removeEventListener('mousedown', dismissWhenOutside);
            document.removeEventListener('focusin', dismissWhenOutside);
        };
    }, [dismissCompanyNameSearch, inputId, isDepartmentSearchEnabled]);

    const applyEntity = React.useCallback((entity: IEntity, updateOrganizationNumber = false) => {
        const containingFormItem = '.ms-business-sign-up__form-item-CompanyAddress';
        setValueAndTriggerEvent('#CompanyName', entity.Name || '');
        if (updateOrganizationNumber) {
            setValueAndTriggerEvent('#Department', entity.OrganizationNumber || '');
        }
        setValueAndTriggerEvent(`${containingFormItem} #company_addressstreet`, entity.BusinessAddresses?.Addresses?.[0] || '');
        setValueAndTriggerEvent(`${containingFormItem} #company_addresszipcode`, entity.BusinessAddresses?.ZipCode || '');
        setValueAndTriggerEvent(`${containingFormItem} #company_addresscity`, entity.BusinessAddresses?.City || '');
        setMatchedEntity(undefined);
        setNameMatches([]);
        setActiveCompanyNameMatchIndex(-1);
        lastDepartmentSearchStatus.current = 'idle';
        lastCompanyNameSearchStatus.current = 'idle';
        setDepartmentSearchStatus('idle');
        setCompanyNameSearchStatus('idle');
    }, []);

    const applyMatchedEntity = React.useCallback(() => {
        if (matchedEntity) {
            applyEntity(matchedEntity);
        }
    }, [applyEntity, matchedEntity]);

    const focusCompanyNameResult = React.useCallback((index: number) => {
        const result = document.getElementById(`company-name-result-${index}`) as HTMLButtonElement | null;
        result?.focus();
        setActiveCompanyNameMatchIndex(index);
    }, []);

    const onCompanyNameResultKeyDown = React.useCallback((event: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            if (index < nameMatches.length - 1) {
                focusCompanyNameResult(index + 1);
            }
        } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            if (index === 0) {
                document.getElementById('CompanyName')?.focus();
            } else {
                focusCompanyNameResult(index - 1);
            }
        } else if (event.key === 'Escape') {
            event.preventDefault();
            dismissCompanyNameSearch();
            document.getElementById('CompanyName')?.focus();
        }
    }, [dismissCompanyNameSearch, focusCompanyNameResult, nameMatches.length]);

    React.useEffect(() => {
        if (inputId !== 'CompanyName' || !isDepartmentSearchEnabled) {
            return;
        }

        const focusResultFromInput = (event: KeyboardEvent) => {
            if (
                document.activeElement !== document.getElementById('CompanyName') ||
                companyNameSearchStatus !== 'matches' ||
                !nameMatches.length ||
                (event.key !== 'ArrowDown' && event.key !== 'ArrowUp')
            ) {
                return;
            }

            event.preventDefault();
            focusCompanyNameResult(event.key === 'ArrowDown' ? 0 : nameMatches.length - 1);
        };

        document.addEventListener('keydown', focusResultFromInput);
        return () => document.removeEventListener('keydown', focusResultFromInput);
    }, [companyNameSearchStatus, focusCompanyNameResult, inputId, isDepartmentSearchEnabled, nameMatches.length]);

    const firstChild = isAddressInput ? React.cloneElement(React.Children.toArray((input as React.ReactElement).props.children)[0], {
        isRequired: true
    }) : undefined;
    let inputNew = input;

    if (isAddressInput && firstChild) {
        inputNew = React.cloneElement(input as React.ReactElement, undefined, [firstChild]);
    }

    if (inputId === 'Department') {
        const onChangeOriginal: (event: React.ChangeEvent<HTMLInputElement>) => void = (input as React.ReactElement).props.onChange;
        inputNew = React.cloneElement(input as React.ReactElement, {
            isRequired: true,
            onChange: isDepartmentSearchEnabled
                ? (event: React.ChangeEvent<HTMLInputElement>) => {
                      if ((event.nativeEvent as CustomEvent).detail?.preventAutoCompleteSearch) {
                          onChangeOriginal(event);
                          return;
                      }
                      const organizationNumber = event.target.value;
                      const orgNumber = organizationNumber.replace(/\D/g, '').slice(-9);
                      const searchId = latestSearch.current + 1;
                      latestSearch.current = searchId;
                      setMatchedEntity(undefined);
                      lastDepartmentSearchStatus.current = 'idle';

                      if (orgNumber.length === 9) {
                          setDepartmentSearchStatus('searching');
                          void doDepartmentSearch(organizationNumber, searchId);
                      } else {
                          doDepartmentSearch.cancel();
                          setDepartmentSearchStatus('idle');
                      }
                      onChangeOriginal(event);
                  }
                : onChangeOriginal
        });
    }

    if (inputId === 'CompanyName' && isDepartmentSearchEnabled) {
        const onChangeOriginal: (event: React.ChangeEvent<HTMLInputElement>) => void = (input as React.ReactElement).props.onChange;
        inputNew = React.cloneElement(input as React.ReactElement, {
            onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
                if ((event.nativeEvent as CustomEvent).detail?.preventAutoCompleteSearch) {
                    onChangeOriginal(event);
                    return;
                }

                const companyName = event.target.value;
                const searchId = latestCompanyNameSearch.current + 1;
                latestCompanyNameSearch.current = searchId;
                setNameMatches([]);
                setActiveCompanyNameMatchIndex(-1);
                lastCompanyNameSearchStatus.current = 'idle';

                if (companyName.trim().length >= 2) {
                    setCompanyNameSearchStatus('searching');
                    void doCompanyNameSearch(companyName, searchId);
                } else {
                    doCompanyNameSearch.cancel();
                    setCompanyNameSearchStatus('idle');
                }
                onChangeOriginal(event);
            },
            onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => {
                if (event.key === 'Escape') {
                    dismissCompanyNameSearch();
                    return;
                }

                if (!nameMatches.length || companyNameSearchStatus !== 'matches') {
                    return;
                }

                if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                    event.preventDefault();
                    focusCompanyNameResult(event.key === 'ArrowDown' ? 0 : nameMatches.length - 1);
                } else if (event.key === 'Enter' && activeCompanyNameMatchIndex >= 0) {
                    event.preventDefault();
                    applyEntity(nameMatches[activeCompanyNameMatchIndex], true);
                }
            },
            role: 'combobox',
            'aria-autocomplete': 'list',
            'aria-expanded': companyNameSearchStatus === 'matches',
            'aria-controls': 'company-name-search-results',
            'aria-activedescendant': activeCompanyNameMatchIndex >= 0 ? `company-name-result-${activeCompanyNameMatchIndex}` : undefined
        });
    }

    const itemWrapper = inputId === 'Department' || inputId === 'CompanyName' || inputId === 'CompanyAddress'
        ? {
            ...wrapper,
                className: [
                    wrapper.className,
                    inputId === 'Department' && `department-search-${isDepartmentSearchEnabled ? 'enabled' : 'disabled'}`,
                    inputId === 'Department' && isDepartmentSearchEnabled && departmentSearchStatus !== 'idle' && 'department-search-open',
                    inputId === 'CompanyName' && isDepartmentSearchEnabled && 'company-name-search-enabled',
                    inputId === 'CompanyName' && isDepartmentSearchEnabled && companyNameSearchStatus !== 'idle' && 'company-name-search-open',
                    inputId === 'CompanyAddress' && `address-search-${isAddressSearchEnabled ? 'enabled' : 'disabled'}`
                ].filter(Boolean).join(' ')
        }
        : wrapper;

    // Hide label and error message if rendering address section
    return (
        <Node {...itemWrapper} style={{ display: inputId === 'ProspectType' ? 'none' : undefined }}>
            {!isAddressInput && (
                inputId === 'Department'
                    ? React.cloneElement(label as React.ReactElement,{ text: resources?.organizationIdLabel })
                    : label
            )}
            {!isAddressInput && errorMessage}
            {inputId === 'Department' &&
                isDepartmentSearchEnabled &&
                <span className='msc-address-form__input-search-btn' aria-hidden='true' />}
            {inputId === 'CompanyName' && isDepartmentSearchEnabled && <span className='msc-address-form__input-search-btn' aria-hidden='true' />}
            {inputNew}
            {inputId === 'Department' && isDepartmentSearchEnabled && departmentSearchStatus !== 'idle' && (
                <div className={`department-search__popover department-search__popover--${departmentSearchStatus}`} aria-live='polite'>
                    {departmentSearchStatus === 'searching' && (
                        <span className='department-search__searching'>
                            <span className='department-search__status-spinner' aria-hidden='true' />
                            {resources?.organizationSearchSearchingText}
                        </span>
                    )}
                    {departmentSearchStatus === 'no-match' && <span>{resources?.organizationSearchNoMatchText}</span>}
                    {departmentSearchStatus === 'match' && matchedEntity && (
                        <button type='button' className='department-search__result' onClick={applyMatchedEntity}>
                            <span className='department-search__result-status'>{resources?.organizationSearchMatchText}</span>
                            <strong>{matchedEntity.Name}</strong>
                            <span className='department-search__result-number'>{matchedEntity.OrganizationNumber}</span>
                            <span className='department-search__result-indicator' aria-hidden='true'>›</span>
                        </button>
                    )}
                </div>
            )}
            {inputId === 'CompanyName' && isDepartmentSearchEnabled && companyNameSearchStatus !== 'idle' && (
                <div
                    className={`department-search__popover department-search__popover--${companyNameSearchStatus}`}
                    id='company-name-search-results'
                    aria-live='polite'
                    role={companyNameSearchStatus === 'matches' ? 'listbox' : undefined}
                >
                    {companyNameSearchStatus === 'searching' && (
                        <span className='department-search__searching'>
                            <span className='department-search__status-spinner' aria-hidden='true' />
                            {resources?.organizationSearchSearchingText}
                        </span>
                    )}
                    {companyNameSearchStatus === 'no-match' && <span>{resources?.companyNameSearchNoMatchText}</span>}
                    {companyNameSearchStatus === 'matches' && nameMatches.map((entity, index) => (
                        <button
                            type='button'
                            className={`department-search__result${activeCompanyNameMatchIndex === index ? ' department-search__result--active' : ''}`}
                            key={`${entity.OrganizationNumber || 'name'}-${entity.Name}-${index}`}
                            id={`company-name-result-${index}`}
                            role='option'
                            aria-selected={activeCompanyNameMatchIndex === index}
                            onFocus={() => setActiveCompanyNameMatchIndex(index)}
                            onKeyDown={event => onCompanyNameResultKeyDown(event, index)}
                            onClick={() => applyEntity(entity, true)}
                        >
                            <strong>{entity.Name}</strong>
                            <span className='department-search__result-number'>{entity.OrganizationNumber}</span>
                            <span className='department-search__result-indicator' aria-hidden='true'>›</span>
                        </button>
                    ))}
                </div>
            )}
        </Node>
    );
};

const validateDepartmentAddressAnd = (props: IBusinessSignUpViewProps) => {
    if (typeof window === 'undefined' || !window.document) {
        return false;
    }

    const department = document.getElementById('Department') as HTMLInputElement | undefined;
    const street = document.getElementById('company_addressstreet') as HTMLInputElement | undefined;
    const zipCode = document.getElementById('company_addresszipcode')  as HTMLInputElement | undefined;
    const city = document.getElementById('company_addresscity')  as HTMLInputElement | undefined;
    const phone = document.getElementById('company_addressphone')  as HTMLInputElement | undefined;

    return department?.value && street?.value && zipCode?.value && city?.value && phone?.value;
};

const Grouped: Record<string, string[]> = {
    Company: ['CompanyName', 'Department', 'JobTitle', 'CompanySize'],
    ContactPerson: ['FirstName', 'LastName', 'CompanyEmail'],
    Address: ['CompanyAddress'],
    Other: ['ProspectType', 'Comment']
};

const getGroupInfo = (resources: IBusinessSignUpResources): Record<string, string> => {
    return {
        ContactPerson: resources.contactPersonLabel,
        Company: resources.companyLabel,
        Address: resources.companyAddressLabel,
        Other: resources.additionalInfoLabel
    };
};

const BusinesSignUpView: React.FC<IBusinessSignUpViewProps & { resources: IBusinessSignUpResources }> = props => {
    const { businessSignUp, ContainerWrapper, heading, businessSignUpForm, resources } = props;
    const featureConfig = props.config as IBusinessSignUpFeatureConfig;

    const { FormWrapper, buttons, items, errorMessage, disclaimer } = businessSignUpForm;

    React.useEffect(() => {
        // Auto check the buyer radio button
        if ((document.querySelector('#ProspectType-buyer') as HTMLInputElement)) {
            (document.querySelector('#ProspectType-buyer') as HTMLInputElement).click();
        }

        // This view is only rendered on the business registration page.
        void GTM.beginSignUpEventAsync();
    }, []);

    const validDepartmentAddressAnd = validateDepartmentAddressAnd(props);

    // Keep the organization-number field compact beside the company-name field.
    if (items) {
        const departmentIndex = items.findIndex(item => item.id === 'Department');
        if (departmentIndex > -1) {
            items[departmentIndex].wrapper.className = items[departmentIndex].wrapper.className.replace('width-50', 'width-25');
        }
    }

    // Group items based on the Grouped configuration
    const groupedItems: Record<string, IBusinessSignUpItem[]> = {
        Company: [],
        ContactPerson: [],
        Address: [],
        Other: []
    };

    if (items) {
        items.forEach((item) => {
            // Try to find the group this item belongs to
            const group = Object.keys(Grouped).find(x => Grouped[x].includes(item.id)) || 'Other';
            if (!groupedItems[group]) {
                groupedItems[group] = [];
            }
            groupedItems[group].push(item);
        });
    }

    const groupInfo = getGroupInfo(resources);

    return (
        <Module {...businessSignUp}>
            <Node {...ContainerWrapper}>
                {heading}
                <Node {...FormWrapper}>
                    {groupedItems && Object.keys(groupedItems).map(group => (
                            <React.Fragment key={group}>
                                <h3 className='ms-business-sign-up__form-section-heading'>{groupInfo[group]}</h3>
                                {group === 'Company' && featureConfig.enableDepartmentSearch && (
                                    <div className='ms-business-sign-up__company-search-info' role='note'>
                                        {resources.companySearchInfoText}
                                    </div>
                                )}
                                {groupedItems[group].map((item: IBusinessSignUpItem) => (
                                <BusinessSignUpItem key={item.id} {...item} {...props} inputId={item.id} />
                            ))}
                        </React.Fragment>
                    ))}
                    {errorMessage &&
                        errorMessage.map((error: React.ReactNode, index: number) => {
                            return <React.Fragment key={index}>{error}</React.Fragment>;
                        })}
                    {buttons &&
                        buttons.map((button: React.ReactNode, index: number) => {
                            let newButton = button;
                            if ((button as React.ReactElement).key === 'continue') {
                                // Take the onClick handler from props and add another handler after it to send GTM event
                                const onClickHandler = (button as React.ReactElement).props.onClick;
                                const isDisabled = (button as React.ReactElement).props.disabled || !validDepartmentAddressAnd;
                                newButton = React.cloneElement(button as React.ReactElement, {
                                    disabled: isDisabled,
                                    onClick: async (event: React.MouseEvent<HTMLButtonElement>) => {
                                        if (onClickHandler) {
                                            await GTM.signUpEventAsync();
                                            await onClickHandler(event);
                                        }
                                    }
                                });
                            }
                            return <React.Fragment key={index}>{newButton}</React.Fragment>;
                        })}
                    {disclaimer}
                </Node>
            </Node>
        </Module>
    );
};

export default BusinesSignUpView;
